import { Toolbar } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { batch, useDispatch, useSelector } from "react-redux";
import { CustomTooltip, QuotaLimitBanner, VersioningDialog, VerticalDivider } from "../../../common/components";
import { checkValidity, CompanyTokenConsumption, DropDownMenu, ImageButton, ProfileMenu } from "../..";
import styles from "./Navbar.module.css";
import { CommonMenu, CompanyMenu, MentionMenu, ProjectManagementMenu, ProjectMenu } from "../../components/menus";
import {
	environmentAllowed,
	environmentSwitch,
	Flags,
	hasPermission,
	isFeatureEnabled,
	isSegFeatureEnabled,
	Permissions,
	SegFlags,
	translate,
} from "../../../common/providers";
import { setContextTourExploration, setOpenProductTourMenu, setPortfolioInfo } from "../../../common/slice";
import { useApi } from "../../../common/hooks";
import { ProjectService } from "../../../api";

const ADMIN_COMPANY = "VEOLIA EAU";

const menuIds = {
	companyMenu: "company-menu",
	mentionMenu: "mention-menu",
	projectMenu: "project-menu",
	profileMenu: "profile-menu",
	languageMenu: "language-menu",
	helpMenu: "help-menu",
};

const Navbar = ({
	commonMenus = [],
	company = "",
	currentOpenedMenu = "",
	homeMenus = [],
	isLogged = false,
	mentionsMenus = [],
	onClickLogout,
	onClickVersion,
	onClose,
	onCloseAiConsumption,
	onHotRelog,
	onIncludes,
	onIsSelected,
	onOpen,
	onOpenAiConsumption,
	onOpenHover,
	onSelect,
	openAiConsumption,
	openVersion = false,
	project = null,
	projectManagementMenus = [],
	projectMenus = [],
	setOpenVersion,
}) => {
	const [companyMenuDisabled, setCompanyMenuDisabled] = useState(false);
	const isLoadingFlags = useSelector(({ context }) => context.loadingFlags);
	const isLoadingUser = useSelector(({ context }) => context.loadingUser);
	const companies = useSelector(({ context }) => context.companies);
	const loggedPhysicalCompId = useSelector(({ context }) => context.loggedPhysicalComp);
	const relogCompanies = useSelector(({ context }) => context.companies);
	const projectId = useSelector(({ context }) => context.project?.id);
	const portfolioInfo = useSelector(({ context }) => context.portfolioInfo);
	const { call: getProjectPortfolio } = useApi(ProjectService.getProjectPortfolio);
	const dispatch = useDispatch();
	useEffect(() => {
		if (
			(!Array.isArray(homeMenus) ||
				homeMenus.length <= 0 ||
				homeMenus.filter((m) => checkValidity(m)).length <= 0) &&
			(!company ||
				(project !== null &&
					(!Array.isArray(project?.teams) || project.teams.filter((c) => c.id !== company.id).length <= 0)) ||
				(!project && !hasPermission(Permissions.PLATFORM_ADMIN) && relogCompanies.length <= 1))
		) {
			setCompanyMenuDisabled(true);
		} else {
			setCompanyMenuDisabled(false);
		}
	}, [homeMenus, project, company, relogCompanies]);
	useEffect(() => {
		if (projectId && (!portfolioInfo || portfolioInfo.projectId !== projectId) && environmentAllowed(["veolia"])) {
			getProjectPortfolio({ projectId }).then((data) => dispatch(setPortfolioInfo({ projectId, ...data })));
		}
	}, [projectId, dispatch, portfolioInfo, getProjectPortfolio]);
	const companyList = useMemo(() => {
		function filterList(list) {
			let nonAdminIncluded = false;
			return list.filter((obj) => {
				if (obj.companyName === ADMIN_COMPANY) {
					return true;
					// eslint-disable-next-line no-else-return
				} else if (!nonAdminIncluded) {
					nonAdminIncluded = true;
					return true;
				}
				return false;
			});
		}
		return environmentSwitch({
			main: project?.teams || relogCompanies || [],
			veolia: filterList(project?.teams || relogCompanies || []),
		});
	}, [project, relogCompanies]);
	const handleOpenProductTourMenu = () => {
		if (isSegFeatureEnabled(SegFlags.PRODUCT_TOUR)) {
			batch(() => {
				dispatch(setContextTourExploration(false));
				dispatch(setOpenProductTourMenu(true));
			});
		}
	};
	return (
		<>
			<Toolbar className={styles.navbar}>
				<span className={styles.navbar__mainMenus}>
					<ImageButton />
				</span>
				{isLogged && (
					<>
						{project && (
							<span className={styles.navbar__projectManagementMenus}>
								<DropDownMenu
									autoTranslate="no"
									data-testid="projectMenu.btn"
									menuName={menuIds.projectMenu}
									open={currentOpenedMenu === menuIds.projectMenu}
									selected={onIncludes(projectManagementMenus)}
									title={translate("navigation:project.project-configuration")}
									onClose={onClose}
									onOpen={onOpen}
									onOpenHover={onOpenHover}
								>
									<ProjectManagementMenu
										projectMenus={projectManagementMenus}
										onIsSelected={onIsSelected}
										onSelect={onSelect}
									/>
								</DropDownMenu>
							</span>
						)}
						{(project && (
							<span className={styles.navbar__projectMenus} data-cy="navbar__projectMenus">
								<ProjectMenu
									currentOpenedMenu={currentOpenedMenu}
									projectMenus={projectMenus}
									onClose={onClose}
									onIncludes={onIncludes}
									onIsSelected={onIsSelected}
									onOpen={onOpen}
									onOpenHover={onOpenHover}
									onSelect={onSelect}
								/>
							</span>
						)) ||
							(!hasPermission(Permissions.PLATFORM_ADMIN) && !isLoadingFlags && !isLoadingUser && (
								<span className={styles.navbar__projectMenus}>
									<CommonMenu
										commonMenus={commonMenus}
										currentOpenedMenu={currentOpenedMenu}
										onClose={onClose}
										onIncludes={onIncludes}
										onIsSelected={onIsSelected}
										onOpen={onOpen}
										onOpenHover={onOpenHover}
										onSelect={onSelect}
									/>
								</span>
							))}
						<span className={styles.navbar__userMenus} data-cy="navbar__userMenus">
							{project && (
								<>
									{isFeatureEnabled(Flags.MENTIONS) && environmentAllowed(["prod", "dev", "qa"]) && (
										<MentionMenu
											data-testid="mentionMenu.btn"
											mentions={[]}
											menuName={menuIds.mentionMenu}
											open={currentOpenedMenu === menuIds.mentionMenu}
											project={project}
											selected={onIncludes(mentionsMenus)}
											onClose={onClose}
											onOpen={onOpen}
										/>
									)}
								</>
							)}
							{company &&
								environmentSwitch({
									main: true,
									veolia: (project?.teams || relogCompanies)?.some(
										(cmp) => cmp.companyName === ADMIN_COMPANY
									),
								}) && (
									<DropDownMenu
										autoTranslate="no"
										data-testid="companyMenu.btn"
										disabled={companyMenuDisabled}
										menuName={menuIds.companyMenu}
										open={currentOpenedMenu === menuIds.companyMenu}
										selected={onIncludes(homeMenus)}
										title={environmentSwitch({
											main:
												project?.companyLoggedToProject ||
												companies.find((c) => c.id === company.id)?.companyName ||
												companies.find((c) => c.id === loggedPhysicalCompId)?.companyName,
											veolia: translate("navigation:home.administration"),
										})}
										onClose={onClose}
										onOpen={onOpen}
										onOpenHover={onOpenHover}
									>
										<CompanyMenu
											companies={environmentSwitch({
												main: project?.teams || relogCompanies || [],
												veolia: companyList,
											})}
											company={company}
											homeMenus={homeMenus}
											onHotRelog={onHotRelog}
											onIsSelected={onIsSelected}
											onSelect={onSelect}
										/>
									</DropDownMenu>
								)}
							<VerticalDivider className={styles.divider} />
							{project && (
								<div className={styles.nameContainer}>
									<CustomTooltip title={project?.name}>
										<span className={styles.project__name}>{project?.name}</span>
									</CustomTooltip>
									<VerticalDivider className={styles.divider} />
								</div>
							)}
							<ProfileMenu
								menuName={menuIds.profileMenu}
								open={currentOpenedMenu === menuIds.profileMenu}
								onClickLogout={onClickLogout}
								onClickVersion={onClickVersion}
								onClose={onClose}
								onIsSelected={onIsSelected}
								onOpen={onOpen}
								onOpenAiConsumption={onOpenAiConsumption}
								onOpenHover={onOpenHover}
								onOpenProductTourMenu={handleOpenProductTourMenu}
								onSelect={onSelect}
							/>
						</span>
					</>
				)}
			</Toolbar>
			<VersioningDialog handleClose={() => setOpenVersion(false)} open={openVersion} />
			<CompanyTokenConsumption open={openAiConsumption} onClose={onCloseAiConsumption} />
			<QuotaLimitBanner onOpenAiConsumption={onOpenAiConsumption} />
		</>
	);
};
export default Navbar;
